import { DirectiveBinding, DirectiveOptions } from "vue/types/options";
import { isFunction } from "lodash";
import { forceNextTick } from "@/util/force-next-tick";

type InfiniteScrollDirectiveBinding = DirectiveBinding & {
  value?: { callback: () => void; root?: () => HTMLElement };
};

const elementObserversMap = new WeakMap<
  HTMLElement,
  { intersection: IntersectionObserver; mutation: MutationObserver }
>();

function inserted(
  el: HTMLElement,
  binding: InfiniteScrollDirectiveBinding
): void {
  if (binding.value === undefined) {
    return;
  }

  if (!isFunction(binding.value.callback) || !isFunction(binding.value.root)) {
    return;
  }

  forceNextTick(() => {
    if (el.lastElementChild === null) {
      return;
    }

    // Constructing IntersectionObserver causes an immediate callback call
    let isItImmediateIntersectionObserverCallbackCall = true;

    const intersectionObserver = new IntersectionObserver(
      entries => {
        const maybeIntersectingEntry = entries.find(
          entry => entry.isIntersecting
        );

        // There's an intersecting entry found, and it's not an immediate callback call
        if (
          !isItImmediateIntersectionObserverCallbackCall &&
          maybeIntersectingEntry !== undefined
        ) {
          binding.value.callback();
        }

        if (isItImmediateIntersectionObserverCallbackCall) {
          isItImmediateIntersectionObserverCallbackCall = false;
        }
      },
      {
        threshold: 0.1,
        root: binding.value.root() ?? el
      }
    );

    intersectionObserver.observe(el.lastElementChild);

    // Once the child HTML elements are changed, we want to re-observe the last child element
    const mutationObserver = new MutationObserver(mutationList => {
      const maybeChildListMutation = mutationList.find(
        mutation => mutation.type === "childList"
      );

      if (maybeChildListMutation === undefined) {
        return;
      }

      intersectionObserver.disconnect();

      if (el.lastElementChild !== null) {
        intersectionObserver.observe(el.lastElementChild);
      }
    });

    mutationObserver.observe(el, { childList: true });

    elementObserversMap.set(el, {
      intersection: intersectionObserver,
      mutation: mutationObserver
    });
  });
}

function unbind(el: HTMLElement): void {
  const observer = elementObserversMap.get(el);

  if (observer !== undefined) {
    observer.mutation.disconnect();
    observer.intersection.disconnect();
  }
}

const InfiniteScroll: DirectiveOptions = {
  inserted,
  unbind
};

export default InfiniteScroll;
